import { useId, useState } from 'react';
import { useAuth } from '@dental-staffing-platform/auth';
import { z } from 'zod';
import {
  payoutSetupAdminActionInput,
  payoutSetupStatus,
  type PayoutSetupAdminActionInput,
  type PayoutSetupQuery,
} from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Row,
  Select,
  SkeletonList,
  Stack,
  Textarea,
} from '@dental-staffing-platform/ui';
import { payoutAdminClient } from '../lib/payout-onboarding-client.js';
import { AdminApiError } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';

const retainedSchema = z.object({
  professionalId: z.string().min(1),
  input: payoutSetupAdminActionInput,
});
type Retained = z.infer<typeof retainedSchema>;
const label = (value: string) => value.replaceAll('_', ' ');
function load(key: string) {
  try {
    const parsed = retainedSchema.safeParse(JSON.parse(sessionStorage.getItem(key) ?? 'null'));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
export function PayoutOnboarding() {
  const { user } = useAuth();
  const key = `dpp-payout-operator:${user?.organizationId}:${user?.id}`;
  return <PayoutQueue key={key} storageKey={key} />;
}
function PayoutQueue({ storageKey }: { storageKey: string }) {
  const filterId = useId();
  const [pending, setPending] = useState(() => load(storageKey)),
    [selected, setSelected] = useState<string | null>(
      () => load(storageKey)?.professionalId ?? null
    );
  const [search, setSearch] = useState(''),
    [status, setStatus] = useState(''),
    [page, setPage] = useState(1),
    [query, setQuery] = useState<PayoutSetupQuery>({ page: 1, pageSize: 20 });
  const state = useAsync(() => payoutAdminClient.list({ ...query, page }), [query, page]);
  const retain = (value: Retained | null) => {
    setPending(value);
    try {
      if (value) sessionStorage.setItem(storageKey, JSON.stringify(value));
      else sessionStorage.removeItem(storageKey);
    } catch {
      /* In-memory retry remains available. */
    }
  };
  return (
    <Stack gap={24}>
      <PageHeader
        title="Payout onboarding"
        subtitle="Coordinate hosted setup, review submitted checklists and record corrections."
      />
      <Card>
        <h2>Recorded setup, separate from payment</h2>
        <p>
          WorkMarket setup is completed in its hosted account. This queue records DPP review and
          provider worker references. It cannot verify a bank account or send contractor payouts.
          Never copy tax IDs, bank numbers, identity documents or passwords into these notes.
        </p>
      </Card>
      {pending && selected !== pending.professionalId && (
        <Card role="region" aria-label="Retained payout setup update">
          <p>
            An earlier update is not confirmed. Open it to retry the same request or review its
            saved state.
          </p>
          <Button onClick={() => setSelected(pending.professionalId)}>
            Resume retained setup update
          </Button>
        </Card>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const parsed = payoutSetupStatus.safeParse(status);
          setPage(1);
          setQuery({
            page: 1,
            pageSize: 20,
            ...(search.trim() ? { search: search.trim() } : {}),
            ...(parsed.success ? { status: parsed.data } : {}),
          });
        }}
      >
        <Row style={{ flexWrap: 'wrap', alignItems: 'end' }}>
          <Field label="Search payout setups" htmlFor={`${filterId}-search`}>
            <Input
              id={`${filterId}-search`}
              type="search"
              value={search}
              maxLength={100}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Field>
          <Field label="Setup status" htmlFor={`${filterId}-status`}>
            <Select
              id={`${filterId}-status`}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              {payoutSetupStatus.options.map((value) => (
                <option key={value} value={value}>
                  {label(value)}
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit">Apply setup filters</Button>
          <Button variant="secondary" type="button" onClick={state.reload}>
            Refresh setup queue
          </Button>
        </Row>
      </form>
      {state.loading ? (
        <SkeletonList count={3} />
      ) : state.error ? (
        <Card>
          <p role="alert">{state.error}</p>
          <Button onClick={state.reload}>Retry setup queue</Button>
        </Card>
      ) : state.data?.items.length ? (
        <>
          <Stack gap={16}>
            {state.data.items.map((profile) => (
              <Card key={profile.id}>
                <h2>{profile.professionalName}</h2>
                <p style={{ overflowWrap: 'anywhere' }}>
                  Professional reference: {profile.professionalId}
                </p>
                <Row style={{ flexWrap: 'wrap' }}>
                  <Badge>{label(profile.status)}</Badge>
                  <span>{new Date(profile.updatedAt).toLocaleString()}</span>
                  <Button
                    variant="secondary"
                    disabled={!!pending && pending.professionalId !== profile.professionalId}
                    onClick={() => setSelected(profile.professionalId)}
                  >
                    Review payout setup
                  </Button>
                </Row>
              </Card>
            ))}
          </Stack>
          <Pagination
            page={state.data.page}
            pageSize={state.data.pageSize}
            total={state.data.total}
            totalPages={state.data.totalPages}
            onPageChange={setPage}
            itemLabel="setups"
          />
        </>
      ) : (
        <EmptyState title="No matching payout setup requests" />
      )}
      {selected && (
        <SetupDetail
          key={selected}
          id={selected}
          pending={pending}
          retain={retain}
          changed={state.reload}
          close={() => setSelected(null)}
        />
      )}
    </Stack>
  );
}
function SetupDetail({
  id,
  pending,
  retain,
  changed,
  close,
}: {
  id: string;
  pending: Retained | null;
  retain: (value: Retained | null) => void;
  changed: () => void;
  close: () => void;
}) {
  const fieldId = useId();
  const state = useAsync(() => payoutAdminClient.get(id), [id]);
  const [page, setPage] = useState(1);
  const history = useAsync(() => payoutAdminClient.history(id, page), [id, page]);
  const [action, setAction] = useState<PayoutSetupAdminActionInput['action']>(
      pending?.input.action ?? 'start_review'
    ),
    [body, setBody] = useState(pending?.input.body ?? ''),
    [workerReference, setWorkerReference] = useState<string | undefined>(
      pending ? (pending.input.workerReference ?? '') : undefined
    ),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null),
    [notice, setNotice] = useState<string | null>(null),
    [discard, setDiscard] = useState(false);
  const data = state.data;
  async function send(value: Retained) {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    retain(value);
    try {
      await payoutAdminClient.act(value.professionalId, value.input);
      retain(null);
      setBody('');
      setWorkerReference(undefined);
      setNotice('Operator setup update saved.');
      state.reload();
      history.reload();
      changed();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Setup update could not be confirmed.');
      if (cause instanceof AdminApiError && [400, 403, 404, 413, 422].includes(cause.status))
        retain(null);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <Row style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <h2>Payout setup review</h2>
        <Button variant="ghost" disabled={busy} onClick={close}>
          Close setup details
        </Button>
      </Row>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {pending && (
        <section aria-label="Unconfirmed operator setup update">
          <p>
            The earlier update may already be saved. Retry its original request or refresh before
            discarding.
          </p>
          <Button disabled={busy} onClick={() => void send(pending)}>
            Retry same operator update
          </Button>
          <Button variant="secondary" onClick={state.reload}>
            Refresh saved setup
          </Button>
          <label>
            <input
              type="checkbox"
              checked={discard}
              onChange={(e) => setDiscard(e.target.checked)}
            />{' '}
            I checked the saved status and understand the earlier update may already be saved.
          </label>
          <Button
            variant="ghost"
            disabled={!discard || busy}
            onClick={() => {
              retain(null);
              setDiscard(false);
              setError(null);
            }}
          >
            Discard retained operator request
          </Button>
        </section>
      )}
      {state.loading ? (
        <SkeletonList count={2} />
      ) : state.error || !data ? (
        <>
          <p role="alert">{state.error ?? 'Setup could not be loaded.'}</p>
          <Button onClick={state.reload}>Retry setup detail</Button>
        </>
      ) : data.profile ? (
        <>
          <h3>{data.profile.professionalName}</h3>
          <p>
            <Badge>{label(data.profile.status)}</Badge> · Revision {data.profile.revision}
          </p>
          <dl>
            <dt>Account and network checked by professional</dt>
            <dd>
              {data.profile.progress.providerAccountCreated ? 'Reported complete' : 'Not reported'}
            </dd>
            <dt>Tax information submitted by professional</dt>
            <dd>
              {data.profile.progress.taxInformationSubmitted ? 'Reported complete' : 'Not reported'}
            </dd>
            <dt>Payment account added by professional</dt>
            <dd>
              {data.profile.progress.paymentAccountAdded ? 'Reported complete' : 'Not reported'}
            </dd>
            <dt>Provider worker reference</dt>
            <dd>{data.profile.workerReference ?? 'Not recorded'}</dd>
          </dl>
          {data.profile.operatorMessage && (
            <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {data.profile.operatorMessage}
            </p>
          )}
          <p>{data.notice}</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const chosen = data.allowedActions.includes(action) ? action : data.allowedActions[0];
              const parsed = payoutSetupAdminActionInput.safeParse({
                action: chosen,
                body,
                workerReference:
                  (workerReference ?? data.profile?.workerReference ?? '').trim() || null,
                revision: data.profile?.revision ?? 0,
                clientRequestId: crypto.randomUUID(),
              });
              if (!parsed.success) {
                setError(parsed.error.issues[0]?.message ?? 'Review the action.');
                return;
              }
              void send({ professionalId: id, input: parsed.data });
            }}
          >
            <Stack gap={16}>
              <Field label="Setup review action" htmlFor={`${fieldId}-action`}>
                <Select
                  id={`${fieldId}-action`}
                  value={
                    data.allowedActions.includes(action) ? action : (data.allowedActions[0] ?? '')
                  }
                  disabled={busy || !!pending || !data.allowedActions.length}
                  onChange={(e) => {
                    const parsed = payoutSetupAdminActionInput.shape.action.safeParse(
                      e.target.value
                    );
                    if (parsed.success) setAction(parsed.data);
                  }}
                >
                  {data.allowedActions.map((value) => (
                    <option key={value} value={value}>
                      {label(value)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Provider worker reference" htmlFor={`${fieldId}-reference`}>
                <Input
                  id={`${fieldId}-reference`}
                  value={workerReference ?? data.profile.workerReference ?? ''}
                  maxLength={100}
                  disabled={busy || !!pending}
                  placeholder="Provider reference only, no bank or tax number"
                  onChange={(e) => setWorkerReference(e.target.value)}
                />
              </Field>
              <Field label="Shared setup review note" htmlFor={`${fieldId}-note`}>
                <Textarea
                  id={`${fieldId}-note`}
                  required
                  minLength={10}
                  maxLength={1500}
                  disabled={busy || !!pending}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </Field>
              <p>
                This note is visible to the professional. Record the review result and next action.
                Review does not mean provider verification or payout readiness.
              </p>
              <Button type="submit" disabled={busy || !!pending || !data.allowedActions.length}>
                Save operator setup update
              </Button>
            </Stack>
          </form>
        </>
      ) : (
        <p>No setup request exists for this professional.</p>
      )}
      <h3>Setup review history</h3>
      {history.loading ? (
        <SkeletonList count={1} />
      ) : history.error ? (
        <>
          <p role="alert">{history.error}</p>
          <Button onClick={history.reload}>Retry setup review history</Button>
        </>
      ) : history.data?.items.length ? (
        <>
          <Stack gap={12}>
            {history.data.items.map((event) => (
              <article key={event.id}>
                <strong>{label(event.action)}</strong>
                <p>
                  {event.actorName} · {event.actorSide} ·{' '}
                  {new Date(event.recordedAt).toLocaleString()} · Revision {event.revision}
                </p>
                <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{event.body}</p>
              </article>
            ))}
          </Stack>
          <Pagination
            page={history.data.page}
            pageSize={history.data.pageSize}
            total={history.data.total}
            totalPages={history.data.totalPages}
            onPageChange={setPage}
            itemLabel="updates"
          />
        </>
      ) : (
        <p>No setup history.</p>
      )}
    </Card>
  );
}
